import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Modal from 'cccisd-modal';
import Tooltip from 'cccisd-tooltip';
import notification from 'cccisd-notification';
import { getFormattedDate } from 'js/helpers';
import TakeAssessment from '../TakeAssessment';
import ContactForm from '../ManageReporters/ContactForm';
import InviteModal from './InviteModal';
import style from '../style.css';

// Icons
import IconQuill from 'cccisd-icons/quill3';
import IconCopy from 'cccisd-icons/copy3';
import IconCheckmark from 'cccisd-icons/checkmark-circle';
import { IconInProgress } from 'images/icons.js';
import IconStop from 'cccisd-icons/cancel';

const Boilerplate = window.cccisd.boilerplate;

const AssessmentActions = ({ row, setForceReloadQueries, progress, surveyProgress, setSurveyProgress }) => {
    const [lastVisitedDate, setLastVisitedDate] = useState(null);
    const [showContactForm, setShowContactForm] = useState(!row.name);
    const studentName = `${row.assessment.selectedByPawn.fields.firstName} ${row.assessment.selectedByPawn.fields.lastName}`;
    const reporterTypeLower = useMemo(() => row.reporterType?.toLowerCase(), [row.reporterType]);
    const assessmentLink = Boilerplate.url(
        `/sessionPlayer/${row.deploymentId}/${row.assessment.assignmentPlanId}/${row.session.sessionId}/${
            row.session.settings.reporter
        }/${row.pawnId}/${row.pawnHash}?s=${row.session.settings.options.join(',')}`
    );

    useEffect(() => {
        if (progress && progress.length > 0) {
            const sessionProgress = progress.find(
                p => p.pawnId === row.pawnId && p.sessionId === row.session.sessionId
            );
            if (sessionProgress) {
                setLastVisitedDate(sessionProgress.lastVisitedDate);
                const surveyProgressStatus = sessionProgress.surveyProgressStatus;
                if (surveyProgressStatus) {
                    setSurveyProgress(prev => ({
                        ...prev,
                        [`${row.session.sessionId}_${row.pawnId}`]: surveyProgressStatus,
                    }));
                }
            }
        }
    }, [progress, row.pawnId, row.options]);

    const renderStoppedAssessmentList = () => {
        return (
            <div>
                {row.options.map(surveyHandle => {
                    if (
                        surveyProgress &&
                        surveyProgress[`${row.session.sessionId}_${row.pawnId}`] &&
                        surveyProgress[`${row.session.sessionId}_${row.pawnId}`][surveyHandle] === 2
                    ) {
                        return (
                            <div key={surveyHandle}>
                                <span className="text-success">
                                    <IconCheckmark spaceRight />
                                </span>
                                Completed{' '}
                                {getFormattedDate(
                                    surveyProgress[`${row.session.sessionId}_${row.pawnId}`][
                                        surveyHandle + '_lastVisit'
                                    ]
                                )}
                            </div>
                        );
                    }
                    return (
                        <div key={surveyHandle}>
                            <span className="text-danger">
                                <IconStop spaceRight />
                            </span>
                            Stopped {getFormattedDate(row.stopped)}
                        </div>
                    );
                })}
            </div>
        );
    };

    const surveyCompletedCount = useMemo(() => {
        let completedCount = 0;
        for (const surveyHandle of row.options) {
            if (surveyProgress && surveyProgress[`${row.session.sessionId}_${row.pawnId}`]) {
                const surveyStatus = surveyProgress[`${row.session.sessionId}_${row.pawnId}`][surveyHandle];
                if (surveyStatus === 2) {
                    completedCount++;
                }
            }
        }
        return completedCount;
    }, [row.options, surveyProgress, row.session.sessionId, row.pawnId]);

    const renderProgressStatus = () => {
        return (
            <>
                {surveyCompletedCount > 0 ? (
                    <span>
                        {surveyCompletedCount} of {row.options.length} complete | Last visited:{' '}
                        {getFormattedDate(lastVisitedDate)}
                    </span>
                ) : (
                    <span>In Progress | Last visited: {getFormattedDate(lastVisitedDate)}</span>
                )}
            </>
        );
    };

    const renderContactInfoForm = (callback = () => {}) => {
        return (
            <>
                <div className="alert alert-primary" role="alert">
                    {`No reporter information has been provided. Please take a moment to update the reporter's information.`}
                </div>
                <ContactForm
                    action="edit"
                    contactType={row.session.settings.reporter}
                    closeModal={() => {
                        setShowContactForm(false);
                        callback();
                    }}
                    session={row.session}
                    plan={row.assessment}
                    refreshContactsData={() => {}}
                    refreshPlanData={() => {}}
                    pawn={{ pawnId: row.pawnId, pawnHash: row.pawnHash }}
                    initialValues={{ name: row.name, email: row.email }}
                />
            </>
        );
    };

    const handleCopyLink = () => {
        return navigator.clipboard.writeText(assessmentLink).then(
            () => {
                notification({
                    message: 'Copied URL to clipboard',
                    type: 'success',
                });
            },
            err => {
                notification({
                    message: 'Failed to copy URL to clipboard',
                    type: 'error',
                });
            }
        );
    };

    if (row.session.completedDate || surveyCompletedCount === row.options.length) {
        const lastSurveyHandle = row.options[row.options.length - 1];
        /* eslint-disable-next-line */
        const lastSurveyCompletedDate = surveyProgress
            ? surveyProgress[`${row.session.sessionId}_${row.pawnId}`]
                ? surveyProgress[`${row.session.sessionId}_${row.pawnId}`][lastSurveyHandle + '_lastVisit']
                : null
            : null;
        return (
            <>
                <span className="text-success">
                    <IconCheckmark spaceRight />
                </span>
                Completed {row.session.completedDate || getFormattedDate(lastSurveyCompletedDate)}
            </>
        );
    }

    return (
        <div className={style.assessmentActionsWrapper}>
            {row.stopped ? (
                <>{renderStoppedAssessmentList()}</>
            ) : (
                <div>
                    <div className={style.assessmentActions}>
                        <Modal
                            title={showContactForm ? `${row.reporterType} for ${studentName}` : 'Take Assessment'}
                            trigger={
                                <Tooltip
                                    title={`${
                                        row.name
                                            ? `Begin assessment(s) for ${row.name}.`
                                            : `Begin assessment(s) for this ${reporterTypeLower || 'reporter'}.`
                                    }`}
                                >
                                    <button className="btn btn-primary btn-sm" type="button">
                                        <IconQuill /> Take Now
                                    </button>
                                </Tooltip>
                            }
                            beforeClose={() => {
                                setForceReloadQueries();
                            }}
                            size="large"
                            render={({ closeModal }) => {
                                if (showContactForm) {
                                    return renderContactInfoForm();
                                }
                                return (
                                    <TakeAssessment
                                        session={row.session}
                                        reporterName={row.name}
                                        assessmentLink={assessmentLink}
                                        closeModal={closeModal}
                                        studentName={studentName}
                                    />
                                );
                            }}
                        />
                        <InviteModal
                            row={row}
                            assessmentLink={assessmentLink}
                            setForceReloadQueries={setForceReloadQueries}
                        />
                        <Tooltip
                            title={`Copy the link to send the assessment(s) to ${
                                row.name ?? `this ${reporterTypeLower || 'reporter'}`
                            }.`}
                        >
                            {showContactForm ? (
                                <Modal
                                    title={`${row.reporterType} for ${studentName}`}
                                    trigger={
                                        <button className="btn btn-secondary btn-sm" type="button">
                                            <IconCopy /> Copy Link
                                        </button>
                                    }
                                    size="large"
                                >
                                    {renderContactInfoForm(() => handleCopyLink().then(x => setForceReloadQueries()))}
                                </Modal>
                            ) : (
                                <button className="btn btn-secondary btn-sm" type="button" onClick={handleCopyLink}>
                                    <IconCopy /> Copy Link
                                </button>
                            )}
                        </Tooltip>
                    </div>
                    <div className={style.assessmentActionsProgress}>
                        {lastVisitedDate && (
                            <span style={{ fontSize: 'smaller', fontWeight: '300' }}>
                                <span style={{ color: '#154988', marginRight: '0.3em' }}>
                                    <IconInProgress />
                                </span>
                                {renderProgressStatus()}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssessmentActions;

AssessmentActions.propTypes = {
    row: PropTypes.object.isRequired,
    setForceReloadQueries: PropTypes.func.isRequired,
    progress: PropTypes.array.isRequired,
    surveyProgress: PropTypes.object,
    setSurveyProgress: PropTypes.func.isRequired,
};
AssessmentActions.defaultProps = {};
